import { AlertTriangle, CheckCircle2, Loader2, Play, Server, Terminal, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { promptModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useReleases } from '../releases/api'
import { fmtDateTime } from '../shared/format'
import type { Installation } from '../shared/types'
import {
  INSTALLATION_MANAGE_PERM,
  RELEASE_WRITE_PERM,
  UPDATE_PUSH_PERM,
  useAssignImage,
  useCreateImageJob,
  useDecidePush,
  useLinkServer,
  useRequestPush,
  useServers,
  useUpdatePushes,
  type CustomerServer,
  type UpdatePush,
} from './api'
import { ServerSSHModal } from './ServerSSHModal'
import { useVaultRetry } from './useVaultRetry'

const updateTone = (s: string): BadgeTone =>
  s === 'succeeded' || s === 'restored'
    ? 'success'
    : s === 'failed' || s === 'awaiting_restore'
      ? 'danger'
      : s === 'cancelled'
        ? 'neutral'
        : 'info'

const pushTone = (s: UpdatePush['status']): BadgeTone =>
  s === 'succeeded' ? 'success' : s === 'failed' || s === 'rejected' ? 'danger' : s === 'requested' ? 'warning' : 'info'

const run = async (fn: () => Promise<unknown>, done?: string) => {
  try {
    await fn()
    if (done) toast.success(done)
  } catch (e) {
    toast.error(apiErrorMessage(e))
  }
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right">{children}</span>
    </div>
  )
}

/** Kurulumun sürümü, ajanın bildirdiği son güncelleme ve SSH yedek yolu (klinik ADR-0024). */
export function UpdatePanel({ inst }: { inst: Installation }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const releases = useReleases(inst.account_id)
  const assign = useAssignImage(inst.id)
  const createJob = useCreateImageJob(inst.id)
  const images = (releases.data ?? []).filter((r) => r.job_type === 'image' && r.image_digest)
  const u = inst.last_update

  const newJob = async () => {
    const notes = await promptModal({
      title: t('updates.release.newJob'),
      description: t('updates.release.newJobHint'),
      placeholder: t('updates.release.notes'),
    })
    if (notes === null) return
    await run(() => createJob.mutateAsync(notes.trim()), t('updates.release.jobCreated'))
  }

  return (
    <div className="mb-6 grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <CardTitle>{t('updates.release.title')}</CardTitle>
          {can(RELEASE_WRITE_PERM) ? (
            <Button size="sm" variant="outline" onClick={() => void newJob()} disabled={createJob.isPending}>
              {t('updates.release.newJob')}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="divide-y">
          <Row label={t('updates.release.running')}>{inst.last_version || '—'}</Row>
          <Row label={t('updates.release.assigned')}>
            {inst.assigned_release_id ? (
              <span className="inline-flex items-center gap-2">
                {inst.assigned_version || inst.assigned_release_id.slice(0, 8)}
                <Badge tone={inst.assigned_published ? 'success' : 'warning'}>
                  {t(inst.assigned_published ? 'updates.release.published' : 'updates.release.unpublished')}
                </Badge>
              </span>
            ) : (
              '—'
            )}
          </Row>
          {inst.assigned_image_ref ? (
            <Row label={t('updates.release.image')}>
              <span
                className="block max-w-[18rem] truncate font-mono text-xs"
                title={`${inst.assigned_image_ref}@${inst.assigned_image_digest}`}
              >
                {inst.assigned_image_ref}
              </span>
            </Row>
          ) : null}
          {can(INSTALLATION_MANAGE_PERM) ? (
            <div className="pt-3">
              <Select
                aria-label={t('updates.release.assign')}
                value={inst.assigned_release_id ?? ''}
                onChange={(v) =>
                  v !== inst.assigned_release_id &&
                  void run(() => assign.mutateAsync(v), t('updates.release.assignedDone'))
                }
                placeholder={t('updates.release.assign')}
                options={images.map((r) => ({
                  value: r.id,
                  label: `${r.version || r.id.slice(0, 8)} · ${r.published_at ? t('updates.release.published') : t('updates.release.unpublished')}${r.cosign_verified ? '' : ` · ${t('updates.release.unsigned')}`}`,
                }))}
              />
              <p className="text-muted-foreground mt-2 text-xs">{t('updates.release.assignHint')}</p>
            </div>
          ) : null}
          {inst.image_mismatch ? (
            <p className="text-warning-strong flex items-center gap-2 pt-3 text-xs">
              <AlertTriangle className="size-4" aria-hidden />
              {t('updates.release.mismatch')}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('updates.last.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {u ? (
            <div className="divide-y">
              <Row label={t('updates.last.version')}>{u.version}</Row>
              <Row label={t('updates.last.status')}>
                <Badge tone={updateTone(u.status)}>{t(`updates.status.${u.status}`, { defaultValue: u.status })}</Badge>
              </Row>
              {u.step ? (
                <Row label={t('updates.last.step')}>{t(`updates.step.${u.step}`, { defaultValue: u.step })}</Row>
              ) : null}
              {u.error ? <Row label={t('updates.last.error')}>{u.error}</Row> : null}
              <Row label={t('updates.last.at')}>{u.at ? fmtDateTime(u.at) : '—'}</Row>
              {u.status === 'awaiting_restore' ? (
                <p className="text-destructive pt-3 text-xs">{t('updates.last.awaitingRestore')}</p>
              ) : null}
            </div>
          ) : (
            <EmptyState title={t('updates.last.empty')} />
          )}
        </CardContent>
      </Card>

      {can(UPDATE_PUSH_PERM) ? <PushCard inst={inst} /> : null}
    </div>
  )
}

function PushCard({ inst }: { inst: Installation }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const servers = useServers(inst.account_id)
  const pushes = useUpdatePushes(inst.id, true)
  const link = useLinkServer(inst.id)
  const request = useRequestPush(inst.id)
  const decide = useDecidePush(inst.id)
  const vault = useVaultRetry()
  const [sshFor, setSshFor] = useState<CustomerServer | null>(null)
  const server = (servers.data ?? []).find((s) => s.id === inst.customer_server_id)
  const sshReady =
    !!server &&
    !!(server.host || server.ip_address) &&
    !!server.ssh_user &&
    !!server.ssh_host_key_fingerprint &&
    server.has_ssh_key
  const items = pushes.data ?? []
  const open = items.some((p) => p.status === 'requested' || p.status === 'approved' || p.status === 'running')

  const ask = async () => {
    const reason = await promptModal({
      title: t('updates.push.request'),
      description: t('updates.push.requestHint'),
      placeholder: t('updates.push.reason'),
    })
    if (!reason?.trim()) return
    await run(() => request.mutateAsync(reason.trim()), t('updates.push.requested'))
  }

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <Terminal className="size-4" aria-hidden />
            {t('updates.push.title')}
          </CardTitle>
          <p className="text-muted-foreground text-xs">{t('updates.push.hint')}</p>
        </div>
        <Button size="sm" onClick={() => void ask()} disabled={!sshReady || open || request.isPending}>
          {request.isPending ? <Loader2 className="animate-spin" /> : <Play />}
          {t('updates.push.request')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Server className="text-muted-foreground size-4" aria-hidden />
          <div className="min-w-[14rem] flex-1">
            <Select
              aria-label={t('updates.push.server')}
              value={inst.customer_server_id ?? ''}
              placeholder={t('updates.push.server')}
              onChange={(v) =>
                v !== inst.customer_server_id && void run(() => link.mutateAsync(v), t('updates.push.linked'))
              }
              options={(servers.data ?? []).map((s) => ({
                value: s.id,
                label: `${s.name}${s.host || s.ip_address ? ` · ${s.host || s.ip_address}` : ''}`,
              }))}
            />
          </div>
          {server ? (
            <>
              <Badge tone={sshReady ? 'success' : 'warning'}>
                {t(sshReady ? 'updates.push.sshReady' : 'updates.push.sshMissing')}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => setSshFor(server)}>
                {t('updates.push.configure')}
              </Button>
            </>
          ) : null}
        </div>

        {items.length === 0 ? (
          <EmptyState title={t('updates.push.empty')} />
        ) : (
          <Table>
            <THead>
              <TR className="hover:bg-transparent">
                <TH>{t('updates.push.reason')}</TH>
                <TH>{t('updates.last.status')}</TH>
                <TH className="hidden sm:table-cell">{t('updates.push.created')}</TH>
                <TH className="text-right">{t('updates.push.actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((p) => (
                <TR key={p.id}>
                  <TD className="max-w-xs">
                    <div className="truncate">{p.reason}</div>
                    {p.output ? (
                      <details className="mt-1 text-xs">
                        <summary className="text-muted-foreground cursor-pointer">{t('updates.push.output')}</summary>
                        <pre className="bg-muted mt-1 max-h-48 overflow-auto rounded p-2 font-mono whitespace-pre-wrap">
                          {p.output}
                        </pre>
                      </details>
                    ) : null}
                  </TD>
                  <TD>
                    <Badge tone={pushTone(p.status)}>{t(`updates.push.status.${p.status}`)}</Badge>
                  </TD>
                  <TD className="text-muted-foreground hidden tabular-nums sm:table-cell">
                    {fmtDateTime(p.created_at)}
                  </TD>
                  <TD className="text-right">
                    {p.status === 'requested' ? (
                      p.requested_by === user?.id ? (
                        <span className="text-muted-foreground text-xs">{t('updates.push.waitingSecond')}</span>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={decide.isPending}
                            onClick={() =>
                              void run(
                                () => decide.mutateAsync({ id: p.id, approve: false }),
                                t('updates.push.rejected'),
                              )
                            }
                          >
                            <XCircle />
                            {t('updates.push.reject')}
                          </Button>
                          <Button
                            size="sm"
                            disabled={decide.isPending}
                            onClick={() =>
                              void vault.run(async () => {
                                await decide.mutateAsync({ id: p.id, approve: true })
                                toast.success(t('updates.push.approved'))
                              })
                            }
                          >
                            <CheckCircle2 />
                            {t('updates.push.approve')}
                          </Button>
                        </div>
                      )
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </CardContent>
      {sshFor ? <ServerSSHModal server={sshFor} onClose={() => setSshFor(null)} /> : null}
      {vault.modal}
    </Card>
  )
}
